import {
  TARGETS_DISTANCE_MAX_M,
  TARGETS_DISTANCE_MIN_M,
  TARGETS_MIN_SPEED_DEFAULT_MPS,
  TARGETS_MIN_SPEED_MAX_MPS,
  TARGETS_MIN_SPEED_MIN_MPS,
  TARGETS_SIZE_FACTOR_DEFAULT,
  TARGETS_SIZE_FACTOR_MAX,
  TARGETS_SIZE_FACTOR_MIN,
  TARGETS_SPAWN_DELAY_DEFAULT_MS,
  TARGETS_SPAWN_DELAY_MAX_MS,
  TARGETS_SPAWN_DELAY_MIN_MS,
  TARGETS_SPAWN_DISTANCE_M,
  TARGETS_TIP_MIN_T_DEFAULT,
  TARGETS_TIP_MIN_T_MAX,
  TARGETS_TIP_MIN_T_MIN,
  TARGETS_TTL_DEFAULT_MS,
  TARGETS_TTL_MAX_MS,
  TARGETS_TTL_MIN_MS,
} from '../config/constants';
import { getSetting, setSetting, type UserSettings } from '../settings/userSettings';

type NumericKey = {
  [K in keyof UserSettings]: UserSettings[K] extends number ? K : never;
}[keyof UserSettings];

/** Read a finite numeric setting in [min, max] from the store, falling back to `def`. */
function loadNumber(key: NumericKey, min: number, max: number, def: number): number {
  const n = getSetting(key);
  if (!isFinite(n) || n < min || n > max) return def;
  return n;
}

function saveNumber(key: NumericKey, v: number): void {
  setSetting(key, v);
}

export function loadTargetsSizeFactor(): number {
  return loadNumber(
    'targetsSizeFactor',
    TARGETS_SIZE_FACTOR_MIN,
    TARGETS_SIZE_FACTOR_MAX,
    TARGETS_SIZE_FACTOR_DEFAULT,
  );
}

export function saveTargetsSizeFactor(v: number): void {
  saveNumber('targetsSizeFactor', v);
}

export function loadTargetsMinSpeed(): number {
  return loadNumber(
    'targetsMinSpeed',
    TARGETS_MIN_SPEED_MIN_MPS,
    TARGETS_MIN_SPEED_MAX_MPS,
    TARGETS_MIN_SPEED_DEFAULT_MPS,
  );
}

export function saveTargetsMinSpeed(v: number): void {
  saveNumber('targetsMinSpeed', v);
}

export function loadTargetsDistance(): number {
  return loadNumber(
    'targetsDistance',
    TARGETS_DISTANCE_MIN_M,
    TARGETS_DISTANCE_MAX_M,
    TARGETS_SPAWN_DISTANCE_M,
  );
}

export function saveTargetsDistance(v: number): void {
  saveNumber('targetsDistance', v);
}

/** Time (ms) a target stays alive before auto-missing ("scomparsa"). */
export function loadTargetsTtl(): number {
  return loadNumber('targetsTtl', TARGETS_TTL_MIN_MS, TARGETS_TTL_MAX_MS, TARGETS_TTL_DEFAULT_MS);
}

export function saveTargetsTtl(v: number): void {
  saveNumber('targetsTtl', v);
}

/** Delay (ms) between a target resolving and the next spawning ("comparsa"). */
export function loadTargetsSpawnDelay(): number {
  return loadNumber(
    'targetsSpawnDelay',
    TARGETS_SPAWN_DELAY_MIN_MS,
    TARGETS_SPAWN_DELAY_MAX_MS,
    TARGETS_SPAWN_DELAY_DEFAULT_MS,
  );
}

export function saveTargetsSpawnDelay(v: number): void {
  saveNumber('targetsSpawnDelay', v);
}

/** Thrust tip-gate (custom): fraction of the blade near the punta that counts as a thrust. */
export function loadTargetsTipMinT(): number {
  return loadNumber(
    'targetsTipMinT',
    TARGETS_TIP_MIN_T_MIN,
    TARGETS_TIP_MIN_T_MAX,
    TARGETS_TIP_MIN_T_DEFAULT,
  );
}

export function saveTargetsTipMinT(v: number): void {
  saveNumber('targetsTipMinT', v);
}
